export type MeasurementType = 'IC50' | 'EC50' | 'Ki' | 'Kd' | 'pActivity' | 'Other';
export type ActivityUnit = 'M' | 'mM' | 'uM' | 'nM' | 'pM' | 'unitless';
export type ActivityDirection = 'higher' | 'lower';
export type ActivityTransform = 'none' | 'pActivity';

export interface ActivityConfiguration {
  column: string;
  measurementType: MeasurementType;
  unit: ActivityUnit;
  direction: ActivityDirection;
  transform: ActivityTransform;
}

export interface SARSourceRow {
  smiles?: string | null;
  properties?: Record<string, unknown> | null;
}

export interface ValidatedCompound {
  smiles: string;
  activity: number;
  name: string;
}

export interface ActivityPreview {
  valid: number;
  missing: number;
  nonNumeric: number;
  excluded: number;
  compounds: ValidatedCompound[];
}

const UNIT_TO_MOLAR: Record<ActivityUnit, number> = {
  M: 1,
  mM: 1e-3,
  uM: 1e-6,
  nM: 1e-9,
  pM: 1e-12,
  unitless: 1,
};

const STRICT_NUMBER = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

export function parseStrictNumeric(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || !STRICT_NUMBER.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function detectNumericPropertyColumns(rows: SARSourceRow[]): string[] {
  const numericCounts = new Map<string, number>();
  rows.forEach((row) => {
    Object.entries(row.properties || {}).forEach(([key, value]) => {
      if (parseStrictNumeric(value) !== null) {
        numericCounts.set(key, (numericCounts.get(key) || 0) + 1);
      }
    });
  });
  return Array.from(numericCounts.keys()).sort((a, b) => a.localeCompare(b));
}

export function transformActivityValue(value: number, config: ActivityConfiguration): number | null {
  if (!Number.isFinite(value)) return null;
  if (config.transform === 'pActivity') {
    if (config.unit === 'unitless' || value <= 0) return null;
    return -Math.log10(value * UNIT_TO_MOLAR[config.unit]);
  }
  // SAR services treat larger analysis values as stronger. Preserve that invariant
  // without changing the source value: lower-is-stronger measurements are sign-inverted.
  return config.direction === 'lower' ? -value : value;
}

export function buildActivityPreview(
  rows: SARSourceRow[],
  config: ActivityConfiguration,
): ActivityPreview {
  const preview: ActivityPreview = {
    valid: 0,
    missing: 0,
    nonNumeric: 0,
    excluded: 0,
    compounds: [],
  };

  rows.forEach((row, index) => {
    const raw = row.properties?.[config.column];
    if (raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '')) {
      preview.missing += 1;
      return;
    }
    const parsed = parseStrictNumeric(raw);
    if (parsed === null) {
      preview.nonNumeric += 1;
      return;
    }
    if (!row.smiles) {
      preview.excluded += 1;
      return;
    }
    const activity = transformActivityValue(parsed, config);
    if (activity === null) {
      preview.excluded += 1;
      return;
    }
    preview.compounds.push({
      smiles: row.smiles,
      activity,
      name: String(row.properties?.Name || row.properties?.name || `Cpd_${index + 1}`),
    });
    preview.valid += 1;
  });

  return preview;
}

export function buildAnalysisConfiguration(
  config: ActivityConfiguration,
  preview: ActivityPreview,
  sourceFileId: string | number,
  datasetTotal: number,
) {
  return {
    mode: 'activity-dependent',
    source_file_id: String(sourceFileId),
    source_row_count: datasetTotal,
    activity_column: config.column,
    measurement_type: config.measurementType,
    unit: config.unit,
    direction: config.direction,
    transformation: config.transform,
    analysis_direction: 'higher-is-stronger',
    valid_count: preview.valid,
    missing_count: preview.missing,
    non_numeric_count: preview.nonNumeric,
    excluded_count: preview.excluded,
  };
}
